import { Component, inject, signal, OnInit, OnDestroy, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ApiService, showToast } from './api.service';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from './i18n.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PhotoUploadService } from './photo-upload.service';
import { PhotoGalleryService } from './photo-gallery.service';

@Component({
  selector: 'app-job-photos',
  providers: [PhotoGalleryService],
  standalone: true,
  imports: [CommonModule, RouterLink, FormsModule, TranslatePipe],
  template: `
    <div class="min-h-screen flex flex-col pb-24" style="background:#F7FBFD;">
      <header class="px-5 pt-4 pb-4 sticky top-0 z-40 text-white shadow-sm flex items-center gap-3"
              style="background:linear-gradient(135deg,#0F334A 0%,#145B7A 100%);">
        <a [routerLink]="['/job', jobId()]" class="w-10 h-10 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center text-white/90 active:bg-white/15 shrink-0">
          <i class="ti ti-arrow-left text-[20px]"></i>
        </a>
        <div class="flex-1 min-w-0">
          <p class="text-[10px] font-medium text-white/55 uppercase tracking-widest">{{ 'jobRecord' | t }}</p>
          <h1 class="text-lg font-semibold text-white/95 truncate">{{ 'jobPhotos' | t }}</h1>
        </div>
        <span class="text-[10px] font-medium px-2.5 py-1 rounded-full uppercase border bg-white/10 text-white/75 border-white/15">{{ gallery.total() }}</span>
      </header>

      <main class="flex-1 px-4 pt-5 space-y-4">

        <!-- Capture Button -->
        <div class="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
          <h2 class="text-xs font-bold text-[#1A6FA6] uppercase tracking-wider mb-3">{{ 'capturePhoto' | t }}</h2>
          
          <!-- Hidden camera input -->
          <input #cameraInput type="file" accept="image/*" capture="environment" class="hidden"
            (change)="onPhotoSelected($event)">
          <input #galleryInput type="file" accept="image/*" multiple class="hidden"
            (change)="onPhotoSelected($event)">

          @if (feedbackMessage()) {
            <div class="mb-3 rounded-xl border px-3 py-2 text-xs font-semibold"
              [ngClass]="feedbackType() === 'error' ? 'border-rose-100 bg-rose-50 text-rose-700' : 'border-emerald-100 bg-emerald-50 text-emerald-700'">
              {{ feedbackMessage() }}
            </div>
          }
          
          @if (selectedPhotoPreviews().length) {
            <div class="mb-4">
              <div class="grid grid-cols-2 gap-2">
                @for (item of selectedPhotoPreviews(); track item.url; let photoIndex = $index) {
                  <div class="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
                    <img [src]="item.url" [alt]="'Selected photo ' + (photoIndex + 1)" class="w-full h-36 object-cover">
                    <button type="button" (click)="removeSelectedPhoto(photoIndex)" [disabled]="isQueuing()"
                      class="absolute right-2 top-2 w-8 h-8 rounded-full bg-slate-950/70 text-white flex items-center justify-center disabled:opacity-50"
                      [attr.aria-label]="'Remove selected photo ' + (photoIndex + 1)"><i class="ti ti-x"></i></button>
                    <span class="absolute left-2 bottom-2 px-2 py-1 rounded-lg bg-slate-950/70 text-white text-[10px] font-bold">{{ photoIndex + 1 }}</span>
                  </div>
                }
              </div>
              <p class="mt-2 text-xs font-semibold text-slate-500">{{ selectedPhotoPreviews().length }} of 10 photos selected</p>
            </div>
            <input type="text" [(ngModel)]="caption" [disabled]="isQueuing()" maxlength="300" placeholder="Add one comment for these photos (optional)..."
              class="w-full h-12 px-4 rounded-xl border border-slate-300 bg-slate-50 focus:border-[#2B9AD2] outline-none text-sm font-medium mb-3">
            <div class="flex gap-3">
              <button (click)="clearPreview()" [disabled]="isQueuing()" class="flex-1 h-12 rounded-xl font-bold border border-slate-200 text-slate-600 bg-white">
                <i class="ti ti-x"></i> Discard
              </button>
              <button (click)="uploadPhotos()" [disabled]="isQueuing()"
                class="flex-1 h-12 rounded-xl font-bold text-white disabled:opacity-50"
                style="background: linear-gradient(135deg,#2B9AD2,#1A6FA6);">
                <i class="ti ti-upload"></i> {{ isQueuing() ? 'Saving to device...' : 'Upload ' + selectedPhotoPreviews().length + ' photos' }}
              </button>
            </div>
          } @else {
            <div class="grid grid-cols-2 gap-3">
              <button type="button" (click)="cameraInput.click()"
                class="h-20 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 flex flex-col items-center justify-center gap-1 font-semibold text-slate-600 active:border-[#1A6FA6] active:text-[#1A6FA6] transition-all">
                <i class="ti ti-camera text-[28px]"></i><span class="text-xs">Take Photo</span>
              </button>
              <button type="button" (click)="galleryInput.click()"
                class="h-20 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 flex flex-col items-center justify-center gap-1 font-semibold text-slate-600 active:border-[#1A6FA6] active:text-[#1A6FA6] transition-all">
                <i class="ti ti-photo text-[28px]"></i><span class="text-xs">Choose Photos</span>
              </button>
            </div>
          }
        </div>

        <!-- Photos Grid -->
        @if (isLoading()) {
          <div class="flex items-center justify-center py-16">
            <i class="ti ti-loader animate-spin text-[32px] text-[#2B9AD2]"></i>
          </div>
        } @else if (photos().length === 0) {
          <div class="bg-white rounded-2xl p-10 text-center border border-dashed border-slate-200">
            <i class="ti ti-camera text-[48px] text-slate-300 mb-4 block"></i>
            <h3 class="font-bold text-slate-900 mb-1">{{ 'noPhotosYet' | t }}</h3>
            <p class="text-sm text-slate-400">Capture the first site photo.</p>
          </div>
        } @else {
          <div class="grid grid-cols-2 gap-3">
            @for (photo of photos(); track photo.id; let photoIndex = $index) {
              <button type="button" (click)="openPhotoViewer(photoIndex)" class="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100 text-left">
                <img [src]="photo.thumbnail_url || photo.photo_url || photo.photo_data" [alt]="photo.caption || 'Site photo'"
                  class="w-full h-40 object-cover" loading="lazy" decoding="async">
                <div class="p-3">
                  @if (photo.caption) {
                    <p class="text-xs font-bold text-slate-700 mb-1 truncate">{{ photo.caption }}</p>
                  }
                  <p class="text-[10px] text-slate-400">{{ photo.uploader_name }} · {{ photo.created_at | date:'d MMM, h:mm a' }}</p>
                </div>
              </button>
            }
          </div>
        }
        @if (gallery.error()) {
          <div role="alert" class="rounded-xl bg-rose-50 p-3 text-sm text-rose-700">
            {{ gallery.error() }} <button type="button" (click)="gallery.retry()" class="min-h-11 px-3 underline">Retry</button>
          </div>
        }
        @if (gallery.hasMore()) {
          <button type="button" (click)="gallery.loadMore()" [disabled]="gallery.loadingMore()" class="w-full min-h-11 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700">
            {{ gallery.loadingMore() ? 'Loading photos...' : 'Load more photos' }}
          </button>
        }
      </main>

      @if (selectedPhoto()) {
        <div class="fixed inset-0 z-[100] bg-slate-950/95 flex flex-col" (click)="closePhotoViewer()">
          <div class="h-16 px-4 flex items-center justify-between gap-3 text-white border-b border-white/10" (click)="$event.stopPropagation()">
            <div class="min-w-0"><p class="text-[10px] uppercase tracking-widest text-white/45 font-semibold">Job Photo</p><p class="text-sm font-semibold truncate">{{ selectedPhoto()?.caption || 'Site photo' }}</p></div>
            <div class="flex items-center gap-2">
              <button type="button" (click)="downloadSelectedPhoto()" [disabled]="downloadingPhotoId() === selectedPhoto()?.id" class="h-10 px-3 rounded-xl bg-white/10 text-white flex items-center gap-2 disabled:opacity-50"><i [class]="downloadingPhotoId() === selectedPhoto()?.id ? 'ti ti-loader animate-spin' : 'ti ti-download'"></i><span class="text-xs font-semibold">Download</span></button>
              <button type="button" (click)="closePhotoViewer()" class="w-10 h-10 rounded-xl bg-white/10 text-white"><i class="ti ti-x text-xl"></i></button>
            </div>
          </div>
          <div class="relative flex-1 min-h-0 flex items-center justify-center p-3" (click)="$event.stopPropagation()">
            @if (photos().length > 1) {
              <button type="button" (click)="showPreviousPhoto()" class="absolute left-2 z-10 w-11 h-11 rounded-full bg-slate-950/60 border border-white/15 text-white"><i class="ti ti-chevron-left text-xl"></i></button>
              <button type="button" (click)="showNextPhoto()" class="absolute right-2 z-10 w-11 h-11 rounded-full bg-slate-950/60 border border-white/15 text-white"><i class="ti ti-chevron-right text-xl"></i></button>
            }
            <img [src]="selectedPhoto()?.photo_url || selectedPhoto()?.photo_data" [alt]="selectedPhoto()?.caption || 'Job photo'" class="max-w-full max-h-full object-contain rounded-xl">
          </div>
          <div class="px-4 py-4 border-t border-white/10 text-white" (click)="$event.stopPropagation()">
            <p class="text-sm font-semibold">{{ selectedPhoto()?.caption || 'No comment added.' }}</p>
            <p class="text-xs text-white/50 mt-1">{{ selectedPhoto()?.uploader_name || 'Unknown uploader' }} · {{ formatPhotoTime(selectedPhoto()?.created_at) }}</p>
            <p class="text-[10px] text-white/35 mt-1">{{ photoViewerIndex()! + 1 }} of {{ photos().length }} loaded photos</p>
          </div>
        </div>
      }
    </div>
  `
})
export class JobPhotosComponent implements OnInit, OnDestroy {
  api = inject(ApiService);
  route = inject(ActivatedRoute);
  private readonly uploads = inject(PhotoUploadService);
  private readonly destroyRef = inject(DestroyRef);

  readonly gallery = inject(PhotoGalleryService);
  readonly isQueuing = signal(false);
  jobId = signal('');
  photos = this.gallery.photos;
  isLoading = this.gallery.loading;
  selectedPhotoPreviews = signal<Array<{ file: File; url: string }>>([]);
  feedbackMessage = signal('');
  feedbackType = signal<'success' | 'error'>('success');
  photoViewerIndex = signal<number | null>(null);
  downloadingPhotoId = signal<number | null>(null);
  caption = '';

  ngOnInit() {
    this.uploads.saved.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      if (event.modelType === 'Job' && event.referenceId === this.jobId()) {
        this.gallery.add(event.photo);
      }
    });
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const id = params.get('id');
      if (id) { this.clearPreview(); this.photoViewerIndex.set(null); this.jobId.set(id); this.loadPhotos(id); }
    });
  }

  loadPhotos(id: string) { this.gallery.reset(id, 'Job'); }

  onPhotoSelected(event: Event) {
    if (this.isQueuing()) return;
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    input.value = '';
    const remaining = Math.max(0, 10 - this.selectedPhotoPreviews().length);
    const selected = files.slice(0, remaining);
    this.selectedPhotoPreviews.update(current => [...current, ...selected.map(file => ({ file, url: URL.createObjectURL(file) }))]);
    if (files.length > remaining) this.setFeedback('A maximum of 10 photos can be selected at once.', 'error');
    else this.feedbackMessage.set('');
  }

  ngOnDestroy() { this.clearPreview(); }

  clearPreview() {
    this.selectedPhotoPreviews().forEach(item => URL.revokeObjectURL(item.url));
    this.selectedPhotoPreviews.set([]);
    this.caption = '';
  }

  removeSelectedPhoto(index: number) {
    if (this.isQueuing()) return;
    const selected = this.selectedPhotoPreviews();
    const removed = selected[index];
    if (removed) URL.revokeObjectURL(removed.url);
    this.selectedPhotoPreviews.set(selected.filter((_, itemIndex) => itemIndex !== index));
  }

  private setFeedback(message: string, type: 'success' | 'error') {
    this.feedbackMessage.set(message);
    this.feedbackType.set(type);
  }

  async uploadPhotos() {
    const selected = this.selectedPhotoPreviews();
    if (!selected.length || this.isQueuing()) return;
    this.isQueuing.set(true);
    try {
      await this.uploads.enqueue(this.jobId(), 'Job', selected.map(item => item.file), this.caption);
      if (this.selectedPhotoPreviews() === selected) {
        this.clearPreview();
        this.setFeedback('Photos saved to this device and queued. You can use other screens; unfinished uploads resume when you reopen the app.', 'success');
      }
    } catch (error) {
      this.setFeedback(error instanceof Error ? error.message : 'Photos could not be queued.', 'error');
    } finally { this.isQueuing.set(false); }
  }

  selectedPhoto(): any | null {
    const index = this.photoViewerIndex();
    return index === null ? null : this.photos()[index] || null;
  }

  openPhotoViewer(index: number) {
    if (index >= 0 && index < this.photos().length) this.photoViewerIndex.set(index);
  }

  closePhotoViewer() { this.photoViewerIndex.set(null); }

  showPreviousPhoto() {
    const total = this.photos().length;
    const current = this.photoViewerIndex();
    if (total && current !== null) this.photoViewerIndex.set((current - 1 + total) % total);
  }

  showNextPhoto() {
    const total = this.photos().length;
    const current = this.photoViewerIndex();
    if (total && current !== null) this.photoViewerIndex.set((current + 1) % total);
  }

  downloadSelectedPhoto() {
    const photo = this.selectedPhoto();
    if (!photo?.id || this.downloadingPhotoId() !== null) return;
    this.downloadingPhotoId.set(Number(photo.id));
    this.api.downloadPhoto(photo.id).subscribe({
      next: blob => {
        this.downloadingPhotoId.set(null);
        this.savePhotoBlob(blob, `job-photo-${photo.id}`);
      },
      error: () => {
        this.downloadingPhotoId.set(null);
        showToast('error', 'Photo could not be downloaded.');
      }
    });
  }

  private savePhotoBlob(blob: Blob, baseName: string) {
    const extension = blob.type === 'image/png' ? 'png' : blob.type === 'image/webp' ? 'webp' : blob.type === 'image/gif' ? 'gif' : 'jpg';
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = `${baseName}.${extension}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }

  formatPhotoTime(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value || '';
    return new Intl.DateTimeFormat('en-NZ', {
      timeZone: 'Pacific/Auckland', day: 'numeric', month: 'short', year: 'numeric',
      hour: 'numeric', minute: '2-digit', timeZoneName: 'short'
    }).format(date);
  }
}
